import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { FormEvent } from 'react';
import { Unsubscribe } from 'router5/dist/types/base';
import { IntlShape } from 'react-intl';
import { IEventDetailsDTO, IEventsDAO } from '../../../../api/events.dao';
import { IErrorParsing } from '../../../../infrastructure/error-parsing';
import { IRouter } from '../../../../infrastructure/router';
import { Routes } from '../../../../routes-table';
import { IWebsiteDAO } from '../../../../api/website.dao';
import { FooterLink } from './website-form-body/website-footer-links/website-footer-link.presentation';
import { WebsiteFormBodyVM } from './website-form-body/website-form-body.viewmodel';
import TrackingServiceProvider from '../../../../tracking-service/tracking-service-provider';
import { convertBoolTypeForMixPanel, TRACKING_EVENTS } from '../../../../tracking-service/tracking-values';
import { showNotification } from '../../../global/modal/alerts-host/alert-templates';
import { EventExecutionRuntime } from '../../../../domain/jobs/implementations/event-execution-runtime';
import { JobType } from '../../../../api/jobs.dao';
import { IAlertsService } from '../../../../infrastructure/alerts';
import { CONFIGURATION_DOWNLOAD_IN_PROGRESS_TEXT } from './edit-website.texts';

interface EditWebsiteParams {
    subdomain: string;
    footerLinks: FooterLink[];
    preventJoinEnabled: boolean;
    preventJoinBefore: number;
    customCss: string;
    registrationEnabled: boolean;
}

const getWebsiteParams = (formFields: WebsiteFormBodyVM) => {
    return {
        subdomain: formFields.getSubdomain(),
        footerLinks: formFields.getFooterLinks(),
        ...formFields.getPreventJoinBeforeValues(),
        customCss: formFields.getCustomCss(),
        registrationEnabled: formFields.registrationEnabled,
    };
};
class EditWebsiteViewModel {
    @mobx.observable
    private saveError: string;
    @mobx.observable
    public saving: boolean;
    @mobx.observable
    public loading: boolean;
    private subscriptions: Unsubscribe[];
    @mobx.observable
    private isUploadConfigurationModalOpen: boolean;
    @mobx.observable
    private configurationJSONFile;

    private formFields: WebsiteFormBodyVM;
    constructor(
        private readonly accountId: number,
        private readonly event: IEventDetailsDTO,
        private readonly intl: IntlShape,
        private readonly eventsDAO: IEventsDAO = IoC.Container.get(IEventsDAO),
        @IoC.Inject private readonly router = IoC.Container.get(IRouter),
        private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        private readonly websiteDAO: IWebsiteDAO = IoC.Container.get(IWebsiteDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly executionRuntime: EventExecutionRuntime = IoC.Container.get(EventExecutionRuntime),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
    ) {
        this.loading = false;
        this.subscriptions = [];
        this.subscriptions.push(
            this.router.listen(Routes.PassportEdit, async () => {
                await this.fetchEventsDetails();
            })
        );

        this.subscribeToBlueprintJobs();
    }

    public getFormFields() {
        return this.formFields;
    }

    public isSaving() {
        return this.saving;
    }

    public isLoading() {
        return this.loading;
    }

    public getSaveError() {
        return this.saveError;
    }
    @mobx.action
    public setSaveError(value) {
        this.saveError = value;
    }

    @mobx.action
    public setSubdomainInPassportTab = (value: string) => {
        this.formFields.setSubdomain(value);
    };

    @mobx.action
    public fetchEventsDetails = async (): Promise<void> => {
        this.loading = true;
        this.saveError = null;

        try {
            const resp = await this.eventsDAO.getEventDetails({
                eventId: this.event.eventId,
                accountId: this.accountId,
            });
            mobx.runInAction(() => {
                this.formFields = new WebsiteFormBodyVM(
                    {
                        ...resp,
                        organizationName: this.event.accountName,
                        eventId: this.event.eventId,
                        accountId: this.accountId,
                    },
                    this.intl,
                    this.event
                );
                this.formFields.setSubdomainInPassportTab = this.setSubdomainInPassportTab;
            });
        } catch (e) {
            mobx.runInAction(() => {
                this.saveError = this.errorParsingService.extractMessage(e);
            });
        } finally {
            mobx.runInAction(() => {
                this.loading = false;
            });
        }
    };

    @mobx.action
    public updateWebsite = async (e: FormEvent) => {
        e.preventDefault();

        if (!this.getFormFields().validateWebsite()) {
            this.saving = true;
            this.saveError = '';

            try {
                const websiteParams = getWebsiteParams(this.formFields);

                await this.eventsDAO.updateEventWebsite(
                    {
                        accountId: this.accountId,
                        eventId: this.event.eventId,
                    },
                    { ...websiteParams }
                );
                const footerLinksTracking = this.formFields.getFooterLinks().length
                                                ? convertBoolTypeForMixPanel(true) : convertBoolTypeForMixPanel(false);

                this.trackingService.get().track(TRACKING_EVENTS.ADD_FOOTER_LINK, {
                    'Footer Link URL': footerLinksTracking,
                    'Footer Link Label': footerLinksTracking
                });

                mobx.runInAction(() => {
                    this.saving = false;
                });
            } catch (error) {
                mobx.runInAction(() => {
                    this.saveError = this.errorParsingService.extractMessage(error);
                    this.saving = false;
                });
            }
        }
    };

    public getUploadConfigurationModalOpen = (): boolean => {
        return this.isUploadConfigurationModalOpen;
    };

    @mobx.action.bound
    public setUploadConfigurationModalOpen = (value: boolean) => {
        this.isUploadConfigurationModalOpen = value;
        this.configurationJSONFile = null;
    };

    @mobx.action.bound
    public setConfigurationJSONFile = (file: File) => {
        this.configurationJSONFile = file;
    };

    public getConfigurationJSONFileName = () => {
        return this.configurationJSONFile ? this.configurationJSONFile.name : '';
    };

    @mobx.action.bound
    private handleUploadConfigurationJobCompletion = async (): Promise<void> => {
        this.setUploadConfigurationModalOpen(false);
        this.formFields.closeSubWindows();
        this.fetchEventsDetails();
    };

    public uploadConfigurationJSON = async () => {
        const job = this.executionRuntime.getJobByType(JobType.PassportSettingsUpload);
        job.onComplete(this.handleUploadConfigurationJobCompletion);

        try {
            await job.trigger({ file: this.configurationJSONFile });
            this.setUploadConfigurationModalOpen(false);
        } finally {
            this.setConfigurationJSONFile(null);
        }
    };

    public downloadConfigurationJSON = async () => {
        try {
            await this.websiteDAO.downloadConfigurationJSON({
                accountId: this.accountId,
                eventId: this.event.eventId
            });
            showNotification(this.intl.formatMessage(CONFIGURATION_DOWNLOAD_IN_PROGRESS_TEXT));
        } catch (error) {
            this.alertsService.alert({
                message: this.errorParsingService.extractMessage(error)
            });
        }
    };

    private subscribeToBlueprintJobs(): void {
        [
            JobType.EventBlueprintTrigger,
            JobType.MeetingBlueprintTrigger
        ].forEach(job => this.executionRuntime.onComplete(job, this.fetchEventsDetails));
    }
}

export { EditWebsiteViewModel, EditWebsiteParams, getWebsiteParams };
